import React, { useState, useEffect } from 'react';
import { TEAM_ACCESS_PERMISSIONS, getDefaultTeamAccess } from '../constants/teamAccessPermissions';
import './UserCreateModal.css';

// Phone format helpers - Pakistan mobile (03XX XXXXXXX or +92 3XX XXXXXXX)
const getPhoneDigits = (value) => (value || '').replace(/\D/g, '');

const formatPhoneDisplay = (digits, formatType) => {
  const d = getPhoneDigits(digits);
  if (!d) return '';
  if (formatType === 'international') {
    const n = d.startsWith('92') ? d : d.startsWith('0') ? '92' + d.slice(1) : '92' + d;
    if (n.length <= 2) return n.startsWith('92') ? '+92' : d;
    if (n.length <= 5) return `+92 ${n.slice(2)}`;
    return `+92 ${n.slice(2, 5)} ${n.slice(5)}`;
  }
  // local: 0300 1234567
  const local = d.startsWith('92') ? '0' + d.slice(2) : d.startsWith('3') ? '0' + d : d.startsWith('0') ? d : '0' + d;
  if (local.length <= 4) return local;
  return `${local.slice(0, 4)} ${local.slice(4)}`;
};

const formatPhoneForBackend = (digits) => {
  const d = getPhoneDigits(digits);
  if (!d) return '';
  if (d.startsWith('92') && d.length >= 12) return '+' + d;
  if (d.startsWith('0') && d.length >= 11) return '+92' + d.slice(1);
  if (d.startsWith('3') && d.length >= 10) return '+92' + d;
  return d ? '+92' + (d.startsWith('0') ? d.slice(1) : d) : '';
};

// Generate email from name: "John Doe" -> "john.doe@sheenwear.pk" (domain configurable in backend)
const nameToEmail = (name) => {
  if (!name || !name.trim()) return '';
  const slug = name.trim().toLowerCase().replace(/\s+/g, '.').replace(/[^a-z0-9.]/g, '');
  return slug ? `${slug}@sheenwear.pk` : '';
};

const UserCreateModal = ({ isOpen, onClose, onSave, initialRole = 'user', modalTitle = 'Create New User' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: initialRole,
    isActive: true,
    addresses: []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [phoneFormat, setPhoneFormat] = useState('local'); // 'local' | 'international'
  const [emailAutoFromName, setEmailAutoFromName] = useState(true); // auto-fill email from name
  const [teamAccess, setTeamAccess] = useState(() => getDefaultTeamAccess());
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Sync role when initialRole changes (e.g. opening as Add Team Member)
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({ ...prev, role: initialRole }));
      if (initialRole === 'team') setTeamAccess(getDefaultTeamAccess());
    }
  }, [isOpen, initialRole]);

  // Toggle team access permission
  const toggleTeamAccess = (category, label) => {
    const key = `${category}|${label}`;
    setTeamAccess(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // Auto-fill email from name when name changes (if auto mode on)
      if (field === 'name' && emailAutoFromName) {
        const generated = nameToEmail(value);
        if (generated) next.email = generated;
      }
      return next;
    });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Toggle auto email from name
  const handleEmailFromName = () => {
    setEmailAutoFromName(prev => {
      const next = !prev;
      if (next && formData.name.trim()) {
        setFormData(f => ({ ...f, email: nameToEmail(formData.name) }));
      }
      return next;
    });
  };

  // Phone input: format display as user types (0300 1234567 or +92 300 1234567)
  const handlePhoneChange = (e) => {
    const raw = e.target.value;
    const digits = getPhoneDigits(raw);
    const formatted = formatPhoneDisplay(digits, phoneFormat);
    setFormData(prev => ({ ...prev, phone: formatted }));
    if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    const phoneDigits = getPhoneDigits(formData.phone);
    if (phoneDigits) {
      const normalized = phoneDigits.replace(/^92/, '0');
      const valid = /^0?3\d{9}$/.test(normalized) && normalized.length >= 10;
      if (!valid) newErrors.phone = 'Enter valid Pakistan mobile (e.g. 0300 1234567)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Prepare user data - format phone for backend (+923001234567)
      const { confirmPassword, phone, ...rest } = formData;
      const userDataToSend = {
        ...rest,
        phone: phone ? formatPhoneForBackend(phone) : ''
      };
      if (formData.role === 'team') {
        userDataToSend.accessPermissions = teamAccess;
      }
      console.log('💾 Creating new user:', userDataToSend);
      
      await onSave(userDataToSend);
    } catch (error) {
      console.error('❌ Error creating user:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle address changes
  const handleAddressChange = (index, field, value) => {
    const newAddresses = [...formData.addresses];
    newAddresses[index] = { ...newAddresses[index], [field]: value };
    setFormData(prev => ({ ...prev, addresses: newAddresses }));
  };

  // Add new address
  const addAddress = () => {
    setFormData(prev => ({
      ...prev,
      addresses: [...prev.addresses, {
        type: 'home',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA',
        isDefault: prev.addresses.length === 0
      }]
    }));
  };

  // Remove address
  const removeAddress = (index) => {
    const newAddresses = formData.addresses.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, addresses: newAddresses }));
  };

  // Set default address
  const setDefaultAddress = (index) => {
    const newAddresses = formData.addresses.map((addr, i) => ({
      ...addr,
      isDefault: i === index
    }));
    setFormData(prev => ({ ...prev, addresses: newAddresses }));
  };

  // Reset form when modal closes
  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role: initialRole,
      isActive: true,
      addresses: []
    });
    setErrors({});
    setEmailAutoFromName(true);
    setTeamAccess(getDefaultTeamAccess());
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal-content user-create-modal" role="dialog" aria-modal="true" aria-labelledby="user-create-modal-title">
        <div className="modal-header">
          <h3 id="user-create-modal-title">{modalTitle}</h3>
          <button
            type="button"
            onClick={handleClose}
            className="user-create-modal-close"
            aria-label="Close"
            title="Close"
          >
            <i className="fas fa-times" aria-hidden />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="user-create-form">
          <div className="form-section">
            <h4>Basic Information</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={errors.name ? 'error' : ''}
                  placeholder="Enter full name"
                />
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>
              
              <div className="form-group email-input-group">
                <label htmlFor="email">Email *</label>
                <div className="email-input-wrapper">
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => {
                      handleInputChange('email', e.target.value);
                      setEmailAutoFromName(false); // user edited manually
                    }}
                    className={errors.email ? 'error' : ''}
                    placeholder="name@superwear.pk"
                  />
                  <label className="email-auto-toggle">
                    <input
                      type="checkbox"
                      checked={emailAutoFromName}
                      onChange={handleEmailFromName}
                    />
                    <span>Auto from name</span>
                  </label>
                </div>
                <span className="email-format-hint">
                  {emailAutoFromName ? 'Email auto-fills from name (name@superwear.pk)' : 'Edit manually or enable Auto from name'}
                </span>
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group phone-input-group">
                <label htmlFor="phone">Phone</label>
                <div className="phone-input-wrapper">
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    className={errors.phone ? 'error' : ''}
                    placeholder={phoneFormat === 'local' ? '0300 1234567' : '+92 300 1234567'}
                    maxLength={phoneFormat === 'local' ? 12 : 16}
                  />
                  <div className="phone-format-toggle">
                    <button
                      type="button"
                      className={`format-btn ${phoneFormat === 'local' ? 'active' : ''}`}
                      onClick={() => {
                        setPhoneFormat('local');
                        if (formData.phone) {
                          setFormData(prev => ({ ...prev, phone: formatPhoneDisplay(getPhoneDigits(prev.phone), 'local') }));
                        }
                      }}
                      title="0300 1234567"
                    >
                      0300
                    </button>
                    <button
                      type="button"
                      className={`format-btn ${phoneFormat === 'international' ? 'active' : ''}`}
                      onClick={() => {
                        setPhoneFormat('international');
                        if (formData.phone) {
                          setFormData(prev => ({ ...prev, phone: formatPhoneDisplay(getPhoneDigits(prev.phone), 'international') }));
                        }
                      }}
                      title="+92 300 1234567"
                    >
                      +92
                    </button>
                  </div>
                </div>
                <span className="phone-format-hint">
                  {phoneFormat === 'local' ? '0300 1234567' : '+92 300 1234567'} — Backend: +923001234567
                </span>
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="role">Role</label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="team">Team Member</option>
                </select>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <div className="user-create-password-wrap">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={errors.password ? 'error' : ''}
                    placeholder="Enter password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="user-create-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                  </button>
                </div>
                {errors.password && <span className="error-text">{errors.password}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password *</label>
                <div className="user-create-password-wrap">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={errors.confirmPassword ? 'error' : ''}
                    placeholder="Confirm password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="user-create-password-toggle"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                  </button>
                </div>
                {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
              </div>
            </div>
            
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                />
                <span className="checkbox-text">Active Account</span>
              </label>
            </div>

            {/* Team Member Access Permissions - shown when role is team */}
            {formData.role === 'team' && (
              <div className="access-permissions-section">
                <h4>Team Member Access</h4>
                <p className="access-description">Team members have the following access in admin panel:</p>
                <div className="access-categories">
                  {TEAM_ACCESS_PERMISSIONS.map((perm) => (
                    <div key={perm.category} className="access-category">
                      <div className="access-category-name">{perm.category}</div>
                      <div className="access-buttons">
                        {perm.items.map((item) => {
                          const key = `${perm.category}|${item.label}`;
                          const isOn = teamAccess[key] ?? item.defaultOn;
                          return (
                            <button
                              key={key}
                              type="button"
                              className={`access-btn access-toggle ${isOn ? 'access-on' : 'access-off'}`}
                              onClick={() => toggleTeamAccess(perm.category, item.label)}
                              title={isOn ? 'ON - Click to turn OFF' : 'OFF - Click to turn ON'}
                            >
                              {isOn ? '✓' : '✗'} {item.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <div className="section-header">
              <h4>Addresses (Optional)</h4>
              <button type="button" onClick={addAddress} className="add-address-btn">
                + Add Address
              </button>
            </div>
            
            {formData.addresses.map((address, index) => (
              <div key={index} className="address-card">
                <div className="address-header">
                  <select
                    value={address.type}
                    onChange={(e) => handleAddressChange(index, 'type', e.target.value)}
                    className="address-type"
                  >
                    <option value="home">Home</option>
                    <option value="work">Work</option>
                    <option value="other">Other</option>
                  </select>
                  
                  <div className="address-actions">
                    <label className="default-checkbox">
                      <input
                        type="checkbox"
                        checked={address.isDefault}
                        onChange={() => setDefaultAddress(index)}
                      />
                      Default
                    </label>
                    
                    {formData.addresses.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAddress(index)}
                        className="remove-address-btn"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="address-fields">
                  <div className="form-group">
                    <label>Street</label>
                    <input
                      type="text"
                      value={address.street}
                      onChange={(e) => handleAddressChange(index, 'street', e.target.value)}
                      placeholder="Street address"
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        value={address.city}
                        onChange={(e) => handleAddressChange(index, 'city', e.target.value)}
                        placeholder="City"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        value={address.state}
                        onChange={(e) => handleAddressChange(index, 'state', e.target.value)}
                        placeholder="State"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>ZIP Code</label>
                      <input
                        type="text"
                        value={address.zipCode}
                        onChange={(e) => handleAddressChange(index, 'zipCode', e.target.value)}
                        placeholder="ZIP Code"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Country</label>
                    <select
                      value={address.country}
                      onChange={(e) => handleAddressChange(index, 'country', e.target.value)}
                    >
                      <option value="USA">United States</option>
                      <option value="Canada">Canada</option>
                      <option value="UK">United Kingdom</option>
                      <option value="Australia">Australia</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <button type="button" onClick={handleClose} className="cancel-btn">
              Cancel
            </button>
            <button 
              type="submit" 
              className="create-btn"
              disabled={loading}
            >
              {loading ? 'Creating...' : formData.role === 'team' ? 'Create Team Member' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserCreateModal;

